import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  useCreateDdPhase,
  useReleaseDdPhase,
  useRenameDdPhase,
  useSetPhaseDates,
} from '@/api/hooks';
import {
  ApiError,
  Button,
  Chip,
  HelpTip,
  Icon,
  Input,
  RowMenu,
  cn,
  formatDateOnly,
  toYmd,
  todayYmd,
  useToast,
} from '@dataroom/shared';
import type { DdPhaseSummary, SetDdPhaseDatesRequest } from '@dataroom/shared';
import { DeletePhaseDialog } from './PhaseManage';

/**
 * The PHASE PLAN (checklist redesign slice 2): every phase on one compact table at
 * the top of the Checklist tab — its status, start, due date and progress — so no
 * phase's dates sit 20 screens down the page. Start and due are plain date fields,
 * saved when the field is left (or on Enter) with Undo in a toast; a released
 * phase keeps its start. A start of today releases the phase to the seller, so it
 * asks the same "Release to the seller?" as Release now before it is saved.
 * "+ Add phase" adds a row with name and dates in place (a start of today asks too).
 *
 * On a phone each phase is a small stacked card (the same cells, re-laid out).
 */
export function PhasePlan({
  roomId,
  phases,
  onOpenPhase,
  focusDates = false,
  onDatesFocused,
}: {
  roomId: string;
  /** The room's real phases, in order (the phaseless legacy bucket is not a phase). */
  phases: DdPhaseSummary[];
  /** The phase name was chosen: show that phase in the list below. */
  onOpenPhase?: (phaseId: string) => void;
  /** Bring the table into view and focus the first date that can be set — the
   * hand-off after "Release Phase 1 to the seller now?" → Yes. */
  focusDates?: boolean;
  onDatesFocused?: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const focused = useRef(onDatesFocused);
  focused.current = onDatesFocused;

  useEffect(() => {
    if (!focusDates || phases.length === 0) return;
    const root = ref.current;
    const first = root?.querySelector<HTMLInputElement>('input[type="date"]:not(:disabled)');
    root?.scrollIntoView?.({ block: 'start' });
    first?.focus();
    focused.current?.();
  }, [focusDates, phases.length]);

  return (
    <section ref={ref} aria-labelledby="phase-plan-h" className="scroll-mt-24 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="phase-plan-h" className="inline-flex items-center gap-1 text-base font-semibold">
          Phases
          <HelpTip id="admin.checklist.release" />
        </h3>
        {!adding ? (
          <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
            <Icon name="plus" size={14} />
            Add phase
          </Button>
        ) : null}
      </div>
      {phases.length > 0 || adding ? (
        <table
          aria-labelledby="phase-plan-h"
          className="w-full border-collapse text-sm max-md:block md:table-fixed"
        >
          {/* Fixed columns, so the add row's wide fields do not shift the table. */}
          <colgroup className="max-md:hidden">
            <col style={{ width: '19%' }} />
            <col style={{ width: '27%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '19%' }} />
            <col style={{ width: '5%' }} />
          </colgroup>
          <thead className="text-left text-xs font-medium text-ink-2 max-md:hidden">
            <tr className="border-b border-line">
              <th scope="col" className="py-2 pr-3 font-medium">
                Phase
              </th>
              <th scope="col" className="py-2 pr-3 font-medium">
                Status
              </th>
              <th scope="col" className="py-2 pr-3 font-medium">
                Starts
              </th>
              <th scope="col" className="py-2 pr-3 font-medium">
                Due
              </th>
              <th scope="col" className="py-2 pr-3 font-medium">
                Progress
              </th>
              <th scope="col" className="py-2">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="max-md:block max-md:space-y-2">
            {phases.map((phase) => (
              <PhaseRow
                key={phase.phaseId}
                roomId={roomId}
                phase={phase}
                otherPhases={phases.filter((p) => p.phaseId !== phase.phaseId)}
                onOpen={onOpenPhase}
              />
            ))}
            {adding ? <AddPhaseRow roomId={roomId} onDone={() => setAdding(false)} /> : null}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}

const rowClass =
  'border-b border-line align-middle max-md:grid max-md:grid-cols-2 max-md:gap-x-3 max-md:gap-y-2 max-md:rounded-md max-md:border max-md:p-3';
const cellClass = 'py-2 pr-3 max-md:p-0';
const dateInputClass =
  'h-8 w-full max-w-[10rem] rounded border border-input bg-background px-1.5 text-sm disabled:opacity-60 max-md:max-w-none';

/** A "Starts"/"Due" caption shown above a field only on a phone (the table head
 * says it on a desktop). */
function PhoneLabel({ children }: { children: string }) {
  return <span className="mb-0.5 block text-xs font-medium text-ink-2 md:hidden">{children}</span>;
}

/** One phase: name, status (with Release now), start, due, progress and ⋯. */
function PhaseRow({
  roomId,
  phase,
  otherPhases,
  onOpen,
}: {
  roomId: string;
  phase: DdPhaseSummary;
  otherPhases: DdPhaseSummary[];
  onOpen?: (phaseId: string) => void;
}) {
  const setDates = useSetPhaseDates(roomId);
  const rename = useRenameDdPhase(roomId);
  const toast = useToast();
  const [deleting, setDeleting] = useState(false);
  // Renaming: the name cell becomes a field (with Delete in view, too).
  const [renaming, setRenaming] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  // A start typed as today (or earlier) releases the phase: held here until the
  // release is confirmed. Cancel remounts the field (startKey) on the saved value.
  const [startToConfirm, setStartToConfirm] = useState<string | null>(null);
  const [startKey, setStartKey] = useState(0);
  const phaseId = phase.phaseId!;
  const name = phase.name ?? 'Phase';
  const startEditable = !phase.released;
  const savedStart = startEditable ? (toYmd(phase.releaseOn) ?? '') : (toYmd(phase.startOn) ?? '');
  const savedDue = toYmd(phase.completeOn) ?? '';
  const resolved = phase.covered + phase.notApplicable;

  /** Write one end of the range; Undo puts the old value back. */
  const save = (field: 'releaseOn' | 'completeOn', value: string) => {
    const before = field === 'releaseOn' ? savedStart : savedDue;
    if (value === before) return;
    const start = field === 'releaseOn' ? value : savedStart;
    const due = field === 'completeOn' ? value : savedDue;
    const pastStart = field === 'releaseOn' ? pastStartProblem(value) : null;
    if (pastStart) {
      setProblem(pastStart);
      return;
    }
    if (start && due && due < start) {
      setProblem('The due date cannot be before the start date.');
      return;
    }
    setProblem(null);
    // Today's start releases the phase at once — confirm like Release now.
    if (field === 'releaseOn' && value && value <= todayYmd() && !phase.released) {
      setStartToConfirm(value);
      return;
    }
    const write = (body: SetDdPhaseDatesRequest, undo: boolean) =>
      setDates.mutate(
        { phaseId, body },
        undo
          ? {}
          : {
              onSuccess: () =>
                toast({
                  message: `Saved the dates for “${name}”`,
                  action: {
                    label: 'Undo',
                    onSelect: () => write({ [field]: before || null }, true),
                  },
                }),
            },
      );
    write({ [field]: value || null }, false);
  };

  const confirmStart = () => {
    if (!startToConfirm) return;
    setDates.mutate(
      { phaseId, body: { releaseOn: startToConfirm } },
      {
        onSuccess: () => {
          setStartToConfirm(null);
          toast({ message: `Released “${name}” to the seller` });
        },
      },
    );
  };
  const cancelStart = () => {
    setStartToConfirm(null);
    setStartKey((k) => k + 1);
  };

  const serverError =
    setDates.error instanceof ApiError
      ? setDates.error.message
      : rename.error instanceof ApiError
        ? rename.error.message
        : null;
  const saveName = () => {
    const next = draftName.trim();
    if (!next || next === name) {
      setRenaming(false);
      return;
    }
    rename.mutate(
      { phaseId, body: { name: next } },
      {
        onSuccess: () => {
          setRenaming(false);
          toast({ message: `Renamed “${name}” to “${next}”` });
        },
      },
    );
  };

  return (
    <tr className={rowClass} aria-label={name}>
      <th
        scope="row"
        className={cn(cellClass, 'text-left font-semibold max-md:col-start-1 max-md:row-start-1')}
      >
        {renaming ? (
          <span className="flex flex-col gap-1.5 font-normal">
            <Input
              aria-label={`New name for ${name}`}
              value={draftName}
              autoFocus
              disabled={rename.isPending}
              onChange={(e) => setDraftName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveName();
                }
                if (e.key === 'Escape') setRenaming(false);
              }}
              className="h-8"
            />
            <span className="flex flex-wrap items-center gap-1.5">
              <Button size="sm" disabled={rename.isPending || !draftName.trim()} onClick={saveName}>
                {rename.isPending ? 'Saving…' : 'Save'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={rename.isPending}
                onClick={() => setRenaming(false)}
              >
                Cancel
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive"
                disabled={rename.isPending}
                onClick={() => setDeleting(true)}
              >
                Delete phase…
              </Button>
            </span>
          </span>
        ) : onOpen ? (
          <button
            type="button"
            className="rounded-md text-left hover:underline"
            onClick={() => onOpen(phaseId)}
          >
            {name}
          </button>
        ) : (
          name
        )}
      </th>
      <td className={cn(cellClass, 'max-md:col-span-2 max-md:row-start-2')}>
        <PhaseStatus
          roomId={roomId}
          phase={phase}
          startConfirm={
            startToConfirm
              ? { pending: setDates.isPending, onConfirm: confirmStart, onCancel: cancelStart }
              : null
          }
        />
      </td>
      <td className={cn(cellClass, 'max-md:row-start-3')}>
        <PhoneLabel>Starts</PhoneLabel>
        {startEditable ? (
          <DateField
            key={`s-${savedStart}-${startKey}`}
            label={`Start date for ${name}`}
            value={savedStart}
            min={todayYmd()}
            // Held while its release is being confirmed (Confirm or Cancel).
            disabled={setDates.isPending || startToConfirm !== null}
            onCommit={(v) => save('releaseOn', v)}
          />
        ) : (
          <span
            className="whitespace-nowrap text-ink-2"
            title="Already released, so the start is fixed."
          >
            {savedStart ? formatDateOnly(savedStart) : '—'}
          </span>
        )}
      </td>
      <td className={cn(cellClass, 'max-md:row-start-3')}>
        <PhoneLabel>Due</PhoneLabel>
        <DateField
          key={`d-${savedDue}`}
          label={`Due date for ${name}`}
          value={savedDue}
          min={savedStart || undefined}
          disabled={setDates.isPending}
          onCommit={(v) => save('completeOn', v)}
        />
      </td>
      <td className={cn(cellClass, 'whitespace-nowrap text-ink-2 max-md:col-span-2 max-md:row-start-4')}>
        <span className="font-medium tabular-nums text-ink">
          {resolved}/{phase.total}
        </span>
        {phase.missing > 0 ? ` · ${phase.missing} missing` : ''}
        {phase.waitingOnReview > 0 ? ` · ${phase.waitingOnReview} waiting on review` : ''}
        {problem || serverError ? (
          <p role="alert" className="whitespace-normal text-xs text-destructive">
            {problem ?? serverError}
          </p>
        ) : null}
      </td>
      <td className="py-2 text-right max-md:col-start-2 max-md:row-start-1 max-md:p-0">
        <RowMenu
          label={name}
          items={[
            {
              label: 'Rename…',
              icon: 'pen',
              onSelect: () => {
                rename.reset();
                setDraftName(name);
                setRenaming(true);
              },
            },
            { label: 'Delete phase…', icon: 'trash', danger: true, onSelect: () => setDeleting(true) },
          ]}
        />
        {deleting ? (
          <DeletePhaseDialog
            roomId={roomId}
            phase={phase}
            otherPhases={otherPhases}
            onClose={() => setDeleting(false)}
          />
        ) : null}
      </td>
    </tr>
  );
}

/** A date field that writes when it is left or on Enter — not on every keystroke
 * (typing a year passes through "0002", "0020", …). */
function DateField({
  label,
  value,
  min,
  disabled,
  onCommit,
}: {
  label: string;
  value: string;
  min?: string;
  disabled?: boolean;
  onCommit: (value: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const commit = () => {
    if (draft !== value) onCommit(draft);
  };
  return (
    <input
      type="date"
      aria-label={label}
      value={draft}
      min={min}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          commit();
        }
      }}
      className={dateInputClass}
    />
  );
}

/** The inline "Release to the seller?" confirm — releasing reaches the seller and
 * is effectively one-way. Used by Release now and by a start date of today. */
function ReleaseConfirm({
  pending,
  error,
  onConfirm,
  onCancel,
  focusConfirm = false,
}: {
  pending: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  /** Move focus to Confirm (the field that asked is held until it is answered). */
  focusConfirm?: boolean;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (focusConfirm) confirmRef.current?.focus();
  }, [focusConfirm]);
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-ink-2">Release to the seller?</span>
      <Button ref={confirmRef} size="sm" disabled={pending} onClick={onConfirm}>
        {pending ? 'Releasing…' : 'Confirm'}
      </Button>
      <Button variant="ghost" size="sm" disabled={pending} onClick={onCancel}>
        Cancel
      </Button>
      {error ? <span className="text-xs text-destructive">{error}</span> : null}
    </span>
  );
}

/** Released / Opens <date> / Not scheduled — with Release now (confirmed inline)
 * while unreleased. `startConfirm` is the same confirm, asked by a start date of
 * today typed in the row (the write that releases is the row's). */
function PhaseStatus({
  roomId,
  phase,
  startConfirm = null,
}: {
  roomId: string;
  phase: DdPhaseSummary;
  startConfirm?: { pending: boolean; onConfirm: () => void; onCancel: () => void } | null;
}) {
  const release = useReleaseDdPhase(roomId);
  const [confirming, setConfirming] = useState(false);

  if (phase.released) {
    return (
      <Chip tone="certified" icon="check">
        Released to seller
      </Chip>
    );
  }
  if (startConfirm) return <ReleaseConfirm {...startConfirm} focusConfirm />;
  if (confirming) {
    return (
      <ReleaseConfirm
        pending={release.isPending}
        error={release.error instanceof ApiError ? release.error.message : null}
        onConfirm={() =>
          phase.phaseId && release.mutate(phase.phaseId, { onSuccess: () => setConfirming(false) })
        }
        onCancel={() => setConfirming(false)}
      />
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {phase.releaseOn ? (
        <Chip tone="neutral" icon="calendar">
          Opens {formatDateOnly(phase.releaseOn)}
        </Chip>
      ) : (
        <Chip tone="neutral" icon="lock">
          Not scheduled
        </Chip>
      )}
      <Button variant="outline" size="sm" onClick={() => setConfirming(true)}>
        Release now
      </Button>
    </span>
  );
}

/** A start before today cannot be set — a row's start and the add row's alike. */
function pastStartProblem(start: string): string | null {
  return start && start < todayYmd() ? 'The start cannot be before today.' : null;
}

/** "+ Add phase" in place: a name, and optionally its start and due dates. The
 * phase goes to the end of the checklist, unreleased — unless its start is today,
 * which releases it to the seller at once, so that asks the same "Release to the
 * seller?" as a row's start of today before anything is created. */
function AddPhaseRow({ roomId, onDone }: { roomId: string; onDone: () => void }) {
  const createPhase = useCreateDdPhase(roomId);
  const setDates = useSetPhaseDates(roomId);
  const toast = useToast();
  const [name, setName] = useState('');
  const [start, setStart] = useState('');
  const [due, setDue] = useState('');
  const [error, setError] = useState<string | null>(null);
  // A start of today releases the new phase: held until confirmed (an earlier
  // start is refused, as on a row).
  // Cancel keeps the row open and hands focus back to the start field.
  const [confirmingRelease, setConfirmingRelease] = useState(false);
  const startRef = useRef<HTMLInputElement>(null);
  const refocusStart = useRef(false);
  const pending = createPhase.isPending || setDates.isPending;
  const held = pending || confirmingRelease;
  const orderError = start && due && due < start ? 'The due date cannot be before the start date.' : null;

  useEffect(() => {
    if (confirmingRelease || !refocusStart.current) return;
    refocusStart.current = false;
    startRef.current?.focus();
  }, [confirmingRelease]);

  const submit = async (releaseConfirmed = false) => {
    const trimmed = name.trim();
    if (!trimmed || orderError) return;
    setError(null);
    const pastStart = pastStartProblem(start);
    if (pastStart) {
      setError(pastStart);
      return;
    }
    if (start && start <= todayYmd() && !releaseConfirmed) {
      setConfirmingRelease(true);
      return;
    }
    try {
      const created = await createPhase.mutateAsync({ name: trimmed });
      if (start || due) {
        await setDates.mutateAsync({
          phaseId: created.id,
          body: { ...(start ? { releaseOn: start } : {}), ...(due ? { completeOn: due } : {}) },
        });
      }
      toast({
        message: releaseConfirmed
          ? `Added “${trimmed}” and released it to the seller`
          : `Added the phase “${trimmed}”`,
      });
      onDone();
    } catch (err) {
      setConfirmingRelease(false);
      setError(err instanceof ApiError ? err.message : 'Could not add the phase. Try again.');
    }
  };
  const cancelRelease = () => {
    refocusStart.current = true;
    setConfirmingRelease(false);
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void submit();
    }
    if (e.key === 'Escape') onDone();
  };

  return (
    <tr className={cn(rowClass, 'bg-surface-2')}>
      <td className={cn(cellClass, 'max-md:col-span-2')}>
        <Input
          aria-label="New phase name"
          placeholder="Phase name"
          value={name}
          autoFocus
          disabled={held}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={onKey}
          className="h-8"
        />
      </td>
      <td className={cn(cellClass, 'text-xs text-ink-2 max-md:col-span-2')}>
        {confirmingRelease ? (
          <ReleaseConfirm
            pending={pending}
            onConfirm={() => void submit(true)}
            onCancel={cancelRelease}
            focusConfirm
          />
        ) : (
          'Not released until you release it or its start arrives.'
        )}
      </td>
      <td className={cellClass}>
        <PhoneLabel>Starts</PhoneLabel>
        <input
          type="date"
          ref={startRef}
          aria-label="Start date for the new phase"
          value={start}
          min={todayYmd()}
          disabled={held}
          onChange={(e) => setStart(e.target.value)}
          onKeyDown={onKey}
          className={dateInputClass}
        />
      </td>
      <td className={cellClass}>
        <PhoneLabel>Due</PhoneLabel>
        <input
          type="date"
          aria-label="Due date for the new phase"
          value={due}
          min={start || undefined}
          disabled={held}
          onChange={(e) => setDue(e.target.value)}
          onKeyDown={onKey}
          className={dateInputClass}
        />
      </td>
      <td colSpan={2} className={cn(cellClass, 'max-md:col-span-2')}>
        <span className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={held || !name.trim() || orderError !== null}
            onClick={() => void submit()}
          >
            {pending ? 'Adding…' : 'Add phase'}
          </Button>
          <Button variant="ghost" size="sm" disabled={held} onClick={onDone}>
            Cancel
          </Button>
        </span>
        {orderError || error ? (
          <p role="alert" className="text-xs text-destructive">
            {orderError ?? error}
          </p>
        ) : null}
      </td>
    </tr>
  );
}
